// Tela da Gestão do Financeiro na Visão Geral (#6499): Meyryene Peixoto.
//
// Pergunta da tela: o dinheiro do mês está entrando, e o faturamento e a cobrança
// estão em dia? Os números reaproveitam as telas de cada assunto:
//   - faturamento do mês e prazo: a Conferência do Faturamento (mesma busca e regra);
//   - cobrança do dia: a régua de contato humano do Painel de Cobrança (D+4 a D+24);
//   - cancelamentos e acordos: as tabelas das telas de Cancelamentos e Parcelamentos;
//   - inadimplência (regra do bônus) e prazo médio: o catálogo de KPIs, com a meta do
//     mês da aba Metas ou, sem ela, a do plano de bônus (13% e 14 dias);
//   - recebido contra o previsto: o "Dinheiro do período" da aba CEO;
//   - resultado por pessoa: a aba Equipes do Painel Executivo.
// Recuperação da carteira veio com a #6506. Com a #6509: "sem erro" no faturamento (boletos
// cancelados por erro na emissão, pelo motivo em lista fechada) e a retenção (resultado do
// pedido de cancelamento).
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Ban, ClipboardCheck, Coins, Handshake, TrendingUp } from 'lucide-react';
import { useFinanceiroDashboardData } from '../../Dashboard/PainelExecutivo/sections/financeiro/useFinanceiroDashboardData';
import { useKpisDepartamento } from '../../Dashboard/PainelExecutivo/sections/ceo/useCeoDados';
import { useMetasMensais } from '../../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';
import { useEquipesData } from '../../Dashboard/PainelExecutivo/sections/equipes/useEquipesData';
import { TabelaEquipe } from '../../Dashboard/PainelExecutivo/sections/equipes/EquipesTela';
import { calcularDinheiroDoPeriodo } from '../../../src/lib/dashboard/painelCeo';
import {
  META_INADIMPLENCIA_BONUS, META_PRAZO_RECEBIMENTO_DIAS, inadimplenciaRegraBonus, kpiAutomatico, type ContextoKpis, type DefinicaoKpiAuto,
} from '../../../src/lib/dashboard/kpisAutomaticos';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { rotuloDoMes } from '../../../src/lib/dashboard/metasMensais';
import { mesAtualFaturas } from '../../../src/lib/conferenciaFaturamentoDb';
import { resumirConferencia } from '../../../src/lib/conferenciaFaturamento';
import { avaliarKpi, type KpiAvaliado } from '../../../src/lib/visaoGeral/diretoria';
import { META_RECUPERACAO_CARTEIRA, estoqueVencidoEm, prazoDoFaturamento } from '../../../src/lib/visaoGeral/financeiro';
import { somarRecuperacao } from '../../../src/lib/visaoGeral/cobranca';
import { trimestreDe } from '../../../src/lib/visaoGeral/tecnologia';
import { dataVencimentoDe, ehCobranca, faturasDoPainel } from '../../../src/lib/financeiro/definicoesKpi';
import { valorDevido } from '../../../src/lib/statusBoleto';
import { useRecuperacaoDoPeriodo } from '../useCobranca';
import { lerMes } from '../../../src/lib/visaoGeral/periodo';
import { minhasEmissoes } from '../../../src/lib/visaoGeral/faturamento';
import { CartaoRetencao, useErroERetrabalho } from '../blocosFase3';
import { useProducaoDoMes } from '../useFaturamentoDoDia';
import { BlocosDoDia } from '../BlocosDoDia';
import { CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { useAcordosDaSemana, useCancelamentosEmAndamento, useConferenciaDoMes, useReguaDeHoje } from '../useGestaoFinanceiro';
import type { PropsTelaVisaoGeral } from '../tipos';

const reais = (v: number) => formatarValorKpi(v, 'reais');
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** Equipes da Meyryene na aba Equipes. */
const EQUIPES_DA_GESTAO = ['Faturamento', 'Financeiro', 'Pós Venda'];

/** O KPI com a meta do plano de bônus no lugar da sugerida pelo sistema. */
const comMetaDoPlano = (id: string, meta: number): DefinicaoKpiAuto | undefined => {
  const def = kpiAutomatico(id);
  return def && { ...def, metaSugerida: () => meta };
};

/** Meta no cartão: a do mês (aba Metas), a validada ou a do plano de bônus. */
function metaDoCartao(k: KpiAvaliado | null) {
  if (!k || k.carregando || k.meta <= 0) return undefined;
  const rotulo = k.origemMeta === 'mes' ? `meta de ${rotuloDoMes(k.mesMeta)}` : k.origemMeta === 'sugerida' ? 'meta do plano de bônus' : 'meta';
  return { texto: `${rotulo}: até ${formatarValorKpi(k.meta, k.unidade)}`, atingido: k.atingimento };
}

const TelaGestaoFinanceiro: React.FC<PropsTelaVisaoGeral> = ({ pessoa, definicao, periodo, aoMudarMes }) => {
  const { agora, inicioMes, fimMes, janelaEquipes } = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    const fim = periodo.corrente ? new Date() : new Date(ano, mes0 + 1, 0, 23, 59, 59);
    const ultimo = new Date(ano, mes0 + 1, 0);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      agora: fim,
      inicioMes: iso(new Date(ano, mes0, 1)),
      fimMes: iso(ultimo),
      janelaEquipes: { inicio: new Date(ano, mes0, 1), fim: new Date(fim.getFullYear(), fim.getMonth(), fim.getDate(), 23, 59, 59, 999) },
    };
  }, [periodo.mes, periodo.corrente]);

  // ── dados ──
  const fin = useFinanceiroDashboardData();
  const { kpis: kpisSalvos } = useKpisDepartamento();
  const { metas: metasMensais } = useMetasMensais();
  const mesAtual = mesAtualFaturas();
  const mesEscolhido = rotuloDoMes(periodo.mes);
  const conferenciaHoje = useConferenciaDoMes(mesAtual);
  const conferenciaDoMes = useConferenciaDoMes(mesEscolhido === mesAtual ? null : mesEscolhido);
  const regua = useReguaDeHoje();
  const cancelamentos = useCancelamentosEmAndamento();
  const acordos = useAcordosDaSemana();
  // Recuperação da carteira (#6506): trimestre do mês escolhido, até hoje.
  const tri = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    const t = trimestreDe(new Date(ano, mes0, 1));
    const inicio = new Date(`${t.inicio}T00:00:00`);
    const fimTri = new Date(`${t.fimExclusivo}T00:00:00`);
    const agoraTri = new Date();
    return { ...t, inicioData: inicio, fimData: fimTri < agoraTri ? fimTri : agoraTri };
  }, [periodo.mes]);
  const recuperacao = useRecuperacaoDoPeriodo(tri.inicioData, tri.fimData);
  // Mês escolhido em instantes, para o erro de emissão e a retenção (#6509).
  const mesDatas = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    return { inicio: new Date(ano, mes0, 1), fim: new Date(ano, mes0 + 1, 1) };
  }, [periodo.mes]);
  const producaoDoMes = useProducaoDoMes(mesDatas.inicio, null);
  const emitidosNoMes = useMemo(() => (producaoDoMes.emissoes ? minhasEmissoes(producaoDoMes.emissoes, null, mesDatas.inicio, mesDatas.fim).total : null), [producaoDoMes.emissoes, mesDatas]);
  const erroEmissao = useErroERetrabalho(mesDatas.inicio, mesDatas.fim, emitidosNoMes);
  const equipes = useEquipesData(janelaEquipes);

  const temFaturas = fin.dados.length > 0;
  const ctx = useMemo<ContextoKpis>(() => ({
    hoje: agora, faturas: temFaturas ? fin.dados : null, parteVigor: null, metaFaturamentoUsinas: 0,
    comercial: null, marketing: null, geracao: null, paradas: null, energia: null, chamados: null,
  }), [agora, fin.dados, temFaturas]);
  const salvo = (id: string) => kpisSalvos.find(k => k.calculo_automatico === id && k.status === 'validado');
  // Inadimplência é foto de hoje em qualquer mês escolhido (como na Diretoria).
  const ctxHoje = useMemo(() => ({ ...ctx, hoje: new Date() }), [ctx]);
  const inadKpi = avaliarKpi(comMetaDoPlano('fin_inadimplencia_bonus', META_INADIMPLENCIA_BONUS), ctxHoje, salvo('fin_inadimplencia_bonus'), metasMensais);
  const inadimplencia = useMemo(() => (temFaturas ? inadimplenciaRegraBonus(fin.dados, new Date()) : null), [fin.dados, temFaturas]);
  const prazoKpi = avaliarKpi(comMetaDoPlano('fin_prazo', META_PRAZO_RECEBIMENTO_DIAS), ctx, salvo('fin_prazo'), metasMensais);
  // Previsto = boletos que vencem no mês inteiro; recebido = o que já entrou deles.
  const dinheiro = useMemo(() => (temFaturas ? calcularDinheiroDoPeriodo(fin.dados, inicioMes, fimMes, new Date(), null) : null), [fin.dados, temFaturas, inicioMes, fimMes]);

  const resumoHoje = useMemo(() => (conferenciaHoje.carga ? resumirConferencia(conferenciaHoje.carga.resultado) : null), [conferenciaHoje.carga]);
  const cargaDoMes = mesEscolhido === mesAtual ? conferenciaHoje.carga : conferenciaDoMes.carga;
  const prazo = useMemo(() => (cargaDoMes ? prazoDoFaturamento(cargaDoMes.resultado.linhas) : null), [cargaDoMes]);

  const recuperadoTri = recuperacao.dados ? somarRecuperacao(recuperacao.dados.porPessoa.values()).valor : null;
  const estoqueTri = useMemo(() => (temFaturas
    ? estoqueVencidoEm(faturasDoPainel(fin.dados).filter(f => ehCobranca(f) && f.statusDerivado !== 'Parcelado')
        .map(f => ({ statusDerivado: f.statusDerivado, vencimento: dataVencimentoDe(f), data_pgto_boleto: f.data_pgto_boleto ?? null, valor: valorDevido(f) })), tri.inicio)
    : null), [fin.dados, temFaturas, tri.inicio]);
  const taxaRecuperacao = recuperadoTri != null && estoqueTri && estoqueTri.valor > 0 ? recuperadoTri / estoqueTri.valor : null;

  const reguaTotal = regua.pessoas ? regua.pessoas.reduce((t, p) => ({ hoje: t.hoje + p.hoje, atrasadas: t.atrasadas + p.atrasadas, feitas: t.feitas + p.feitasHoje }), { hoje: 0, atrasadas: 0, feitas: 0 }) : null;
  const equipesDaGestao = equipes.unidades.flatMap(u => u.equipes
    .filter(e => EQUIPES_DA_GESTAO.includes(e.departamento) && e.pessoas.length > 0)
    .map(e => ({ chave: `${u.unidade}-${e.departamento}`, titulo: `Unidade ${u.unidade}`, equipe: e })));

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} />

      <BlocosDoDia pessoa={pessoa} perfil="gestao-financeiro" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo={`faturamento de ${mesAtual} e o que vence agora`} colunas={3}>
        <CartaoHoje
          valor={resumoHoje ? `${resumoHoje.faturado.toLocaleString('pt-BR')}/${resumoHoje.base.toLocaleString('pt-BR')}` : null}
          rotulo={`UCs do rateio faturadas em ${mesAtual}`}
          tom="ok"
          para="/financeiro/conferencia-faturamento"
          dica="UCs da planilha de rateio do mês anterior com boleto emitido e não cancelado (Conferência do Faturamento)."
        />
        <CartaoHoje
          valor={resumoHoje ? resumoHoje.naoFaturadoSemJustificativa + resumoHoje.emApuracao : null}
          rotulo="faturas esperando o boleto"
          tom="atencao"
          para="/financeiro/conferencia-faturamento"
          dica={resumoHoje
            ? `Chegaram e ainda não viraram boleto nem foram puladas com motivo (${resumoHoje.emApuracao} em apuração). Fora delas: ${resumoHoje.puladoJustificado} puladas com motivo e ${resumoHoje.faturaNaoRecebida} faturas que ainda não chegaram da Equatorial (${resumoHoje.faturaNaoRecebidaAtrasada} atrasadas).`
            : undefined}
        />
        <CartaoHoje
          valor={resumoHoje ? resumoHoje.limiteProximo + resumoHoje.limiteVencido : null}
          rotulo="perto ou além do limite de emissão"
          tom={resumoHoje && resumoHoje.limiteVencido > 0 ? 'critico' : 'atencao'}
          para="/financeiro/conferencia-faturamento"
          dica={resumoHoje ? `${resumoHoje.limiteVencido} já passaram do limite; ${resumoHoje.limiteProximo} vencem em até 2 dias.` : undefined}
        />
        <CartaoHoje
          valor={reguaTotal ? reguaTotal.hoje + reguaTotal.atrasadas : null}
          rotulo="contatos da régua pendentes"
          tom={reguaTotal && reguaTotal.atrasadas > 0 ? 'critico' : 'atencao'}
          para="/financeiro/esteira"
          dica={reguaTotal ? `${reguaTotal.hoje} para hoje e ${reguaTotal.atrasadas} de dias anteriores (D+4, D+10, D+17 e D+24). ${reguaTotal.feitas} resolvidos hoje.` : undefined}
        />
        <CartaoHoje
          valor={cancelamentos ? cancelamentos.total : null}
          rotulo="cancelamentos em andamento"
          tom={cancelamentos && cancelamentos.respostaAtrasada > 0 ? 'critico' : 'atencao'}
          para="/financeiro/cancelamentos"
          dica={cancelamentos ? `${cancelamentos.respostaAtrasada} com o prazo de resposta ao cliente vencido.` : undefined}
        />
        <CartaoHoje
          valor={acordos ? acordos.semana.qtd : null}
          rotulo="parcelas de acordo vencem nesta semana"
          tom="atencao"
          para="/financeiro/parcelamentos"
          dica={acordos ? `${reais(acordos.semana.valor)} nos próximos 7 dias. ${plural(acordos.emAtraso, 'acordo está', 'acordos estão')} em atraso.` : undefined}
        />
      </SecaoVisaoGeral>
      {(conferenciaHoje.erro || regua.erro) && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{conferenciaHoje.erro || regua.erro}</p>}

      <PainelDetalhe titulo="Cobrança do dia por pessoa" acao={{ rotulo: 'Painel de Cobrança', para: '/financeiro/esteira' }}>
        {regua.pessoas === null ? (
          <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
        ) : regua.pessoas.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum contato da régua pendente nem feito hoje.</p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-400">
                  <th className="px-5 py-2 text-left font-semibold">Pessoa</th>
                  <th className="px-3 py-2 text-right font-semibold">Para hoje</th>
                  <th className="px-3 py-2 text-right font-semibold">De dias anteriores</th>
                  <th className="px-3 py-2 text-right font-semibold">Mais antiga</th>
                  <th className="px-5 py-2 text-right font-semibold">Resolvidos hoje</th>
                </tr>
              </thead>
              <tbody>
                {regua.pessoas.map(p => (
                  <tr key={p.responsavelId ?? 'sem-dono'} className="border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200">
                    <td className="px-5 py-2 whitespace-nowrap">{p.responsavelId ? regua.nomes.get(p.responsavelId) ?? 'Sem nome' : <span className="text-amber-700 dark:text-amber-400">Sem responsável</span>}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.hoje}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${p.atrasadas > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{p.atrasadas}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">{p.maisAntiga ? dataCurta(p.maisAntiga) : '—'}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{p.feitasHoje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">
          Contato humano da régua: D+4 e D+10 (contato), D+17 (notificação extrajudicial) e D+24 (conferência e negativação). "Resolvidos" inclui os dispensados com motivo.
        </p>
      </PainelDetalhe>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={periodo.rotulo} colunas={3}>
        <CartaoIndicador
          titulo="Inadimplência (regra do bônus)"
          valor={inadimplencia ? (inadimplencia.taxa == null ? '—' : pct(inadimplencia.taxa)) : null}
          meta={metaDoCartao(inadKpi)}
          detalhe={inadimplencia ? `Boletos vencidos de ${dataCurta(inadimplencia.janela.inicio)} a ${dataCurta(inadimplencia.janela.fim)} ainda em aberto: ${reais(inadimplencia.vencido)}. Estoque vencido: ${reais(inadimplencia.estoque)}.` : undefined}
          para="/dashboard/painel-executivo/financeiro"
          origem="Vencido há mais de 30 dias e em aberto ÷ valor desses boletos, nos 3 meses anteriores. Foto de hoje, qualquer que seja o mês escolhido."
        />
        <CartaoIndicador
          titulo="Recebido contra o previsto"
          valor={dinheiro ? reais(dinheiro.recebido) : null}
          meta={dinheiro && dinheiro.esperado > 0 ? { texto: `de ${reais(dinheiro.esperado)} previstos (${pct(dinheiro.recebido / dinheiro.esperado)})`, atingido: dinheiro.recebido / dinheiro.esperado } : undefined}
          detalhe={dinheiro ? `Boletos que vencem em ${rotuloDoMes(periodo.mes)}: ainda a vencer ${reais(dinheiro.aVencer)}, vencido sem pagar ${reais(dinheiro.vencido)}.` : undefined}
          para="/financeiro/previsao-recebiveis"
          origem="Boletos pelo vencimento no mês (mesma conta do Dinheiro do período, na aba CEO)."
        />
        <CartaoIndicador
          titulo={`Faturamento no prazo (${mesEscolhido})`}
          valor={prazo ? (prazo.fracao == null ? '—' : pct(prazo.fracao)) : null}
          detalhe={prazo ? `${prazo.noPrazo} de ${prazo.total} UCs faturadas até o limite de emissão; ${prazo.foraDoPrazo} depois do limite e ${prazo.aposVencimento} depois do vencimento da Equatorial.` : undefined}
          composicao={erroEmissao ? [{
            rotulo: 'Sem erro na emissão',
            valor: erroEmissao.taxa == null ? '—' : pct(1 - erroEmissao.taxa),
            destaque: erroEmissao.taxa != null && erroEmissao.taxa > 0.02,
            dica: erroEmissao.mensuravel
              ? `${plural(erroEmissao.erros, 'boleto cancelado', 'boletos cancelados')} por erro na emissão de ${erroEmissao.emitidos} emitidos (meta de erro: até 2%).`
              : `Ainda sem medida: a maior parte dos cancelamentos do mês tem motivo livre, de antes da lista de motivos (29/10/2026).`,
          }] : undefined}
          para="/financeiro/conferencia-faturamento"
          origem="Conferência do Faturamento: só as UCs já faturadas no mês, com prazo calculável."
        />
        <CartaoIndicador
          titulo={`Recuperação da carteira (${tri.rotulo})`}
          valor={recuperadoTri == null || !estoqueTri ? null : taxaRecuperacao == null ? '—' : pct(taxaRecuperacao)}
          meta={taxaRecuperacao != null ? { texto: `meta ${pct(META_RECUPERACAO_CARTEIRA)}`, atingido: taxaRecuperacao / META_RECUPERACAO_CARTEIRA } : undefined}
          detalhe={recuperadoTri != null && estoqueTri ? `${reais(recuperadoTri)} recuperados com contato registrado no trimestre, sobre ${reais(estoqueTri.valor)} vencidos em aberto em ${dataCurta(tri.inicio)} (${plural(estoqueTri.boletos, 'boleto', 'boletos')}).` : undefined}
          para="/financeiro/esteira"
          origem="Recuperado com contato (boleto pago mais de 3 dias depois do vencimento, com contato registrado entre o vencimento e o pagamento, mesma conta da tela da Cobrança) ÷ valor vencido e ainda em aberto no primeiro dia do trimestre."
        />
        <CartaoIndicador
          titulo="Prazo médio de recebimento"
          valor={prazoKpi && !prazoKpi.carregando ? formatarValorKpi(prazoKpi.atual, 'dias') : null}
          meta={metaDoCartao(prazoKpi)}
          detalhe={prazoKpi?.periodo ? `Da emissão do boleto ao pagamento, nas contas de ${prazoKpi.periodo}.` : undefined}
          para="/dashboard/painel-executivo/financeiro"
        />
        <CartaoRetencao inicio={mesDatas.inicio} fim={mesDatas.fim} rotuloMes={mesEscolhido} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo="Resultado por pessoa da equipe" acao={{ rotulo: 'Aba Equipes', para: '/dashboard/painel-executivo/equipes' }}>
          {equipes.erro ? (
            <p className="text-sm text-red-600 dark:text-red-400">{equipes.erro}</p>
          ) : equipes.loading && equipesDaGestao.length === 0 ? (
            <div className="h-32 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : equipesDaGestao.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Ninguém do Faturamento, do Financeiro ou do Pós-venda com unidade cadastrada.</p>
          ) : (
            <div className="space-y-5">
              {equipesDaGestao.map(e => (
                <div key={e.chave}>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{e.titulo}</h4>
                  <TabelaEquipe equipe={e.equipe} carregandoPrazo={equipes.carregandoPrazo} />
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Em {periodo.corrente ? `${rotuloDoMes(periodo.mes)} até hoje` : rotuloDoMes(periodo.mes)}, pela mesma conta da aba Equipes do Painel Executivo.{' '}
            <Link to="/dashboard/painel-executivo/metas" className="font-semibold text-primary hover:underline">Metas do Financeiro</Link>
          </p>
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Conferência do faturamento', para: '/financeiro/conferencia-faturamento', icone: ClipboardCheck },
        { rotulo: 'Painel de Cobrança', para: '/financeiro/esteira', icone: Coins },
        { rotulo: 'Previsão de Recebíveis', para: '/financeiro/previsao-recebiveis', icone: TrendingUp },
        { rotulo: 'Cancelamentos', para: '/financeiro/cancelamentos', icone: Ban },
        { rotulo: 'Acordos', para: '/financeiro/parcelamentos', icone: Handshake },
      ]} />
    </div>
  );
};

export default TelaGestaoFinanceiro;
